// Seat-based cost calculator. Seats are keyed by plan id, or "a:<add-on name>" for add-on products.
export const LS_SEATS = "m365matrix.seats";
export const ADDON_PREFIX = "a:";

export const isAddonKey = (key) => key.startsWith(ADDON_PREFIX);
export const addonKey = (name) => `${ADDON_PREFIX}${name}`;
export const addonName = (key) => key.slice(ADDON_PREFIX.length);

export const serializeSeats = (seats) =>
  Object.entries(seats)
    .filter(([, n]) => n > 0)
    .map(([k, n]) => `${k}=${n}`)
    .join("|");

export const parseSeats = (raw) => {
  const out = {};
  if (!raw) return out;
  raw.split("|").forEach((part) => {
    const i = part.lastIndexOf("=");
    if (i < 1) return;
    const n = parseInt(part.slice(i + 1), 10);
    if (Number.isFinite(n) && n > 0) out[part.slice(0, i)] = Math.min(n, 1_000_000);
  });
  return out;
};

export const readSeats = () => {
  const fromUrl = new URLSearchParams(window.location.search).get("seats");
  if (fromUrl) return parseSeats(fromUrl);
  try {
    return parseSeats(window.localStorage.getItem(LS_SEATS) || "");
  } catch {
    return {};
  }
};

const round2 = (n) => Math.round(n * 100) / 100;

/** Build priced lines + totals. `plans`/`addons` must already be localized for the region. */
export const computeCost = ({ seats, plans, addons, priceMode }) => {
  const planById = Object.fromEntries(plans.map((p) => [p.id, p]));
  const lines = [];
  Object.entries(seats).forEach(([key, qty]) => {
    if (!(qty > 0)) return;
    if (isAddonKey(key)) {
      const name = addonName(key);
      const info = addons.find((a) => a.name.toLowerCase() === name.toLowerCase());
      const unit = info ? info.price : null;
      lines.push({ key, name, kind: "addon", groupId: "addons", unit, estimated: !!info?.estimated, qty, missing: !info, note: info?.note || "" });
      return;
    }
    const plan = planById[key];
    if (!plan) return;
    const unit = plan.price ? plan.price[plan.addon ? "teams" : priceMode] : null;
    lines.push({
      key,
      name: plan.name,
      short: plan.short,
      kind: plan.addon ? "suite" : "plan",
      groupId: plan.group,
      unit,
      estimated: !!plan.estimated,
      qty,
      requires: plan.requires || [],
      priceLabel: plan.priceLabel || "",
    });
  });
  lines.forEach((l) => {
    l.monthly = l.unit === null || l.unit === undefined ? null : round2(l.unit * l.qty);
    l.annual = l.monthly === null ? null : round2(l.monthly * 12);
  });

  const warnings = [];
  lines
    .filter((l) => l.kind === "suite" && l.requires.length)
    .forEach((l) => {
      const baseSeats = l.requires.reduce((sum, id) => sum + (seats[id] || 0), 0);
      if (l.qty > baseSeats) {
        const bases = l.requires.map((id) => planById[id]?.short || id).join(" / ");
        warnings.push({ key: l.key, text: `${l.name}: ${l.qty} seats but only ${baseSeats} base seats (${bases}). Suite add-ons need a matching base license.` });
      }
    });

  const priced = lines.filter((l) => l.monthly !== null);
  const monthly = round2(priced.reduce((s, l) => s + l.monthly, 0));
  return {
    lines,
    monthly,
    annual: round2(monthly * 12),
    seats: lines.reduce((s, l) => s + l.qty, 0),
    estimated: priced.some((l) => l.estimated),
    unpriced: lines.filter((l) => l.monthly === null),
    warnings,
  };
};

export const buildCostCSV = (cost, pricing, priceMode) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const cur = pricing?.currency || "USD";
  const rows = [
    ["License", "Type", `Unit price (${cur}/user/month)`, "Estimated", "Seats", `Monthly (${cur})`, `Annual (${cur})`],
    ...cost.lines.map((l) => [l.name, l.kind, l.unit ?? "", l.estimated ? "yes" : "", l.qty, l.monthly ?? "", l.annual ?? ""]),
    [],
    ["Total", "", "", cost.estimated ? "includes estimated prices" : "", cost.seats, cost.monthly, cost.annual],
    [],
    [`Pricing: ${pricing?.note || ""}. ${priceMode === "teams" ? "With Teams" : "No Teams"} suite pricing. Generated ${new Date().toISOString().slice(0, 10)}.`],
  ];
  return rows.map((r) => r.map(esc).join(",")).join("\n");
};
